<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { NFlex } from 'naive-ui';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import GaiaSpinner from '@/components/design/base/GaiaSpinner.vue';
import GaiaStepFlow from '@/components/design/composite/GaiaStepFlow.vue';
import { type GaiaStepRailItem } from '@/components/design/composite/GaiaStepRail.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaWizardFooter from '@/components/design/composite/GaiaWizardFooter.vue';
import WizardLayout from '@/components/design/layout/WizardLayout.vue';
import GaiaCalendarDatesStep from '@/components/design/domain/contractManagement/calendar/GaiaCalendarDatesStep.vue';
import GaiaCalendarSourcesStep from '@/components/design/domain/contractManagement/calendar/GaiaCalendarSourcesStep.vue';
import GaiaCalendarSetupStep from '@/components/design/domain/contractManagement/calendar/GaiaCalendarSetupStep.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import {
  CALENDAR_WIZARD_STEPS,
  useCalendarWizard,
  type CalendarWizardForm,
  type CalendarWizardStepKey,
} from '@/composables/useCalendarWizard';

/**
 * Create and edit a calendar (Calendar Events mock): the step rail on the
 * left, the active step in the middle, a pinned action bar below. The state
 * machine lives in `useCalendarWizard`; this view is the shell and the step
 * switch. Nothing is written until the last step — the three steps are
 * one form, not three saves.
 */

const {
  form,
  seeds,
  activeStep,
  activeStepDefinition,
  isEditMode,
  title,
  loading,
  loadError,
  saving,
  saveError,
  activeStepBlocker,
  continueDisabled,
  canGoBack,
  continueLabel,
  footerNote,
  isStepComplete,
  isStepAccessible,
  selectStep,
  goBack,
  goNext,
  updateForm,
  hydrate,
  navigateBack,
} = useCalendarWizard();

/**
 * A step's misses are only worth showing once the user has tried to leave it —
 * an untouched step shouldn't greet them in red.
 */
const touchedSteps = ref<Set<CalendarWizardStepKey>>(new Set());

const showBlocker = computed(
  () => activeStepBlocker.value !== null && touchedSteps.value.has(activeStep.value)
);

async function handleNext(): Promise<void> {
  touchedSteps.value = new Set([...touchedSteps.value, activeStep.value]);
  await goNext();
}

watch(activeStep, () => {
  // Re-entering a step the user already tried keeps its blocker visible; a
  // fresh step starts quiet.
  if (!activeStepBlocker.value) {
    const next = new Set(touchedSteps.value);
    next.delete(activeStep.value);
    touchedSteps.value = next;
  }
});

const railItems = computed<GaiaStepRailItem[]>(() =>
  CALENDAR_WIZARD_STEPS.map(step => ({
    key: step.key,
    label: step.title,
    number: step.number,
    description: step.hint,
    done: isStepComplete(step.key) && step.key !== activeStep.value,
    locked: !isStepAccessible(step.key),
  }))
);

const railHint = computed(() =>
  isEditMode.value
    ? 'Every step stays editable. Changes are written when you save.'
    : 'Start in Setup. The rest unlocks once the required fields are filled in.'
);

function onStepForm(next: CalendarWizardForm): void {
  updateForm(next);
}

onMounted(() => {
  void hydrate();
});
</script>

<template>
  <wizard-layout>
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.contractManagement }}</n-breadcrumb-item>
      <n-breadcrumb-item clickable>
        <router-link :to="{ name: 'contract-management-calendar' }">
          {{ navLabels.calendars }}
        </router-link>
      </n-breadcrumb-item>
      <n-breadcrumb-item>{{ title }}</n-breadcrumb-item>
    </template>
    <template #rail>
      <n-flex vertical :size="14" :wrap="false" class="gaia-wizard-rail">
        <n-flex vertical :size="4" :wrap="false">
          <gaia-header :level="1" size="page">{{ title }}</gaia-header>
          <gaia-text
            class="gaia-wizard-rail__hint"
            size="meta"
            tone="subtle"
            :style="{ lineHeight: 1.5 }"
          >
            {{ railHint }}
          </gaia-text>
        </n-flex>
        <gaia-step-flow
          :items="railItems"
          :active="activeStep"
          @select="key => selectStep(key as CalendarWizardStepKey)"
        />
      </n-flex>
    </template>

    <n-flex vertical :size="18" :wrap="false">
      <n-flex vertical :size="4" :wrap="false">
        <gaia-header :level="2">{{ activeStepDefinition.heading }}</gaia-header>
        <gaia-text size="body" tone="tertiary">{{ activeStepDefinition.hint }}</gaia-text>
      </n-flex>

      <gaia-banner v-if="loadError" type="error" title="This calendar couldn’t be loaded">
        {{ loadError }}
        <template #actions>
          <gaia-button type="primary" size="small" @click="hydrate">Retry</gaia-button>
          <gaia-button size="small" @click="navigateBack">Back to calendars</gaia-button>
        </template>
      </gaia-banner>

      <gaia-banner v-if="saveError" type="error">{{ saveError }}</gaia-banner>

      <gaia-banner v-if="showBlocker" type="warning">{{ activeStepBlocker }}</gaia-banner>

      <gaia-spinner v-if="loading" :show="true" :style="{ minHeight: '160px' }" />

      <!-- Kept alive so a picker remembers the labels of what was chosen in it;
           remounted, it holds bare uids it has never searched and shows those. -->
      <keep-alive v-else-if="!loadError">
        <gaia-calendar-setup-step
          v-if="activeStep === 'setup'"
          :form="form"
          :seeds="seeds"
          :touched="touchedSteps.has('setup')"
          @update:form="onStepForm"
        />

        <gaia-calendar-dates-step
          v-else-if="activeStep === 'dates'"
          :form="form"
          :touched="touchedSteps.has('dates')"
          @update:form="onStepForm"
        />

        <gaia-calendar-sources-step v-else :form="form" @update:form="onStepForm" />
      </keep-alive>
    </n-flex>

    <template #footer>
      <gaia-wizard-footer
        :note="footerNote"
        quiet-label="Cancel"
        :back-label="canGoBack ? 'Back' : undefined"
        :next-label="continueLabel"
        :loading="saving"
        :next-disabled="continueDisabled || loading || loadError !== null"
        @quiet="navigateBack"
        @back="goBack"
        @next="handleNext"
      />
    </template>
  </wizard-layout>
</template>
